import React, { useRef, useState } from 'react';
import './RespondToSuggestion.css';

// Static, no-backend mockup of a "v5" /respond page — same content as v3
// (per-person cards, inline "we'll double check..." confirmation, group
// question, closing note) but with two differences: no typewriter reveal
// (everything renders immediately, plain text), and the cards sit in a
// horizontally-scrolling "arcade" you swipe/scroll through one at a time
// instead of a vertical stacked list. Nothing on this page saves anything.
// Visit /respond-v5-preview to view it.

const MOCK_COMMUNITY = 'UK Golfers';
const MOCK_RECIPIENT_FIRST_NAME = 'James';

const MOCK_PEOPLE = [
  {
    userId: 'mock-1',
    firstName: 'Henry',
    age: 36,
    location: 'Brighton',
    lookingFor: 'Society or roll-up group',
    bio: 'Coming back to golf after a long break. Rusty swing, keen attitude, looking for relaxed practice partners.',
    initials: 'H',
    color: '#0ea5e9',
  },
  {
    userId: 'mock-2',
    firstName: 'Alfie',
    age: 42,
    location: 'Birmingham',
    lookingFor: 'Just a casual round, Competition partners',
    bio: 'Retired a couple of years ago and now play three times a week if the weather lets me. Happy to play with anyone friendly.',
    initials: 'A',
    color: '#f97316',
  },
  {
    userId: 'mock-3',
    firstName: 'Charlotte',
    age: 42,
    location: 'Edinburgh',
    lookingFor: 'Just a casual round, Society or roll-up group',
    bio: 'Mid-teens handicap and trying to get it down before the club championship. Always up for a weekend round.',
    initials: 'C',
    color: '#8b5cf6',
  },
  {
    userId: 'mock-4',
    firstName: 'Priya',
    age: 31,
    location: 'Leeds',
    lookingFor: 'Competition partners',
    bio: 'Plays off an 8 handicap and is always looking for someone to push her round on a Saturday morning.',
    initials: 'P',
    color: '#059669',
  },
  {
    userId: 'mock-5',
    firstName: 'Freddie',
    age: 29,
    location: 'Brighton',
    lookingFor: 'Just a casual round',
    bio: 'New to the area and the game — happy to be the worst player in the group as long as everyone is relaxed about it.',
    initials: 'F',
    color: '#d946ef',
  },
];

const ArcadeCard = ({ person, active, cardRef, status, onConnect, onNotNow }) => (
  <div ref={cardRef} className={`respond-arcade-card${active ? ' respond-arcade-card--active' : ''}`}>
    <div className="respond-card" style={{ flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
      <div className="respond-avatar-mock" style={{ background: person.color, width: '84px', height: '84px', fontSize: '1.8rem' }}>
        {person.initials}
      </div>
      <div className="respond-details" style={{ width: '100%' }}>
        <div className="respond-headline" style={{ justifyContent: 'center' }}>
          <strong>{person.firstName}</strong>
          <span className="respond-age">{person.age}</span>
          <span className="respond-meta"> · {person.location}</span>
        </div>
        {person.lookingFor && <div className="respond-meta"><strong>Looking For:</strong> {person.lookingFor}</div>}
        {person.bio && <div className="respond-bio">{person.bio}</div>}

        <div className="respond-actions" style={{ justifyContent: 'center' }}>
          <button type="button" className={status === 'interested' ? 'button' : 'buttonSecondary'} onClick={onConnect}>
            {status === 'interested' ? '✓ Connect' : 'Connect'}
          </button>
          <button type="button" className={status === 'not_interested' ? 'button' : 'buttonSecondary'} onClick={onNotNow}>
            {status === 'not_interested' ? '✓ Not now' : 'Not now'}
          </button>
        </div>

        {status === 'interested' && (
          <div className="respond-v4-hello-sent" style={{ textAlign: 'center' }}>
            We'll double check with {person.firstName}, and then intro you over messenger.
          </div>
        )}
      </div>
    </div>
  </div>
);

const RespondV5Preview = () => {
  const [actionState, setActionState] = useState({});
  const [groupAnswer, setGroupAnswer] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollerRef = useRef(null);
  const cardRefs = useRef([]);

  const interestedCount = Object.values(actionState).filter((v) => v === 'interested').length;
  const interestedNames = MOCK_PEOPLE.filter((p) => actionState[p.userId] === 'interested').map((p) => p.firstName);
  const namesText = interestedNames.length <= 1
    ? (interestedNames[0] || '')
    : `${interestedNames.slice(0, -1).join(', ')} and ${interestedNames[interestedNames.length - 1]}`;

  const scrollToIndex = (i) => {
    const clamped = Math.max(0, Math.min(MOCK_PEOPLE.length - 1, i));
    cardRefs.current[clamped]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    setActiveIndex(clamped);
  };

  const handleScroll = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const scrollerRect = scroller.getBoundingClientRect();
    const centerX = scrollerRect.left + scrollerRect.width / 2;
    let closest = 0;
    let closestDist = Infinity;
    cardRefs.current.forEach((el, i) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const dist = Math.abs((rect.left + rect.width / 2) - centerX);
      if (dist < closestDist) { closestDist = dist; closest = i; }
    });
    setActiveIndex(closest);
  };

  return (
    <div className="respond-page">
      <div className="respond-page-inner">
        <div className="respond-greeting">Hi {MOCK_RECIPIENT_FIRST_NAME}!</div>
        <div className="respond-letter">
          <h1 className="respond-heading">
            These people are keen to connect in {MOCK_COMMUNITY}.
          </h1>
          <div className="respond-intro">
            Would you like us to put you in touch?
          </div>
        </div>

        <div className="respond-arcade-wrap">
          {MOCK_PEOPLE.length > 1 && (
            <button type="button" className="respond-arcade-nav respond-arcade-nav--prev" onClick={() => scrollToIndex(activeIndex - 1)} aria-label="Previous">‹</button>
          )}
          <div className="respond-arcade" ref={scrollerRef} onScroll={handleScroll}>
            {MOCK_PEOPLE.map((p, i) => (
              <ArcadeCard
                key={p.userId}
                person={p}
                active={i === activeIndex}
                cardRef={(el) => { cardRefs.current[i] = el; }}
                status={actionState[p.userId]}
                onConnect={() => setActionState((prev) => ({ ...prev, [p.userId]: 'interested' }))}
                onNotNow={() => setActionState((prev) => ({ ...prev, [p.userId]: 'not_interested' }))}
              />
            ))}
          </div>
          {MOCK_PEOPLE.length > 1 && (
            <button type="button" className="respond-arcade-nav respond-arcade-nav--next" onClick={() => scrollToIndex(activeIndex + 1)} aria-label="Next">›</button>
          )}
        </div>
        <div className="respond-arcade-dots">
          {MOCK_PEOPLE.map((p, i) => (
            <button
              key={p.userId}
              type="button"
              className={`respond-arcade-dot${i === activeIndex ? ' respond-arcade-dot--active' : ''}`}
              onClick={() => scrollToIndex(i)}
              aria-label={`Go to ${p.firstName}`}
            />
          ))}
        </div>

        <div className="respond-letter" style={{ marginTop: '1.5rem' }}>
          <div className="respond-intro-sub" style={{ marginBottom: 0 }}>
            We won't share your contact details (we intro you over messenger).
          </div>
        </div>

        {interestedCount >= 2 && (
          <div className="respond-letter" style={{ marginTop: '1.5rem' }}>
            <div className="respond-intro">Also, would you prefer to be introduced:</div>
            <div className="respond-actions respond-fade-in" style={{ marginTop: '0.9rem' }}>
              <button type="button" className={groupAnswer === true ? 'button' : 'buttonSecondary'} onClick={() => setGroupAnswer(true)}>
                {groupAnswer === true ? '✓ ' : ''}In a group ({namesText})
              </button>
              <button type="button" className={groupAnswer === false ? 'button' : 'buttonSecondary'} onClick={() => setGroupAnswer(false)}>
                {groupAnswer === false ? '✓ ' : ''}1:1 Individually
              </button>
            </div>
          </div>
        )}

        <div className="respond-done respond-fade-in respond-fade-in--slow">
          <button type="button" className="button-fancy" disabled>
            Close window
          </button>
          <div className="respond-done-note">This is a preview — nothing here is saved.</div>
        </div>
      </div>
    </div>
  );
};

export default RespondV5Preview;
